
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AccountDetails } from "./types";
import { AccountDetailsForm } from "./AccountDetailsForm";
import { useNavigate } from "react-router-dom";

interface SignUpFormProps {
  onSuccess: () => void;
}

export function SignUpForm({ onSuccess }: SignUpFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accountData, setAccountData] = useState<AccountDetails>({
    email: "",
    password: "",
    confirmPassword: "",
    username: "",
    displayName: ""
  });
  const navigate = useNavigate();

  const handleSubmit = async (data: AccountDetails) => {
    setIsSubmitting(true);
    try {
      console.log("Starting signup process...");
      
      // Create the user account
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            username: data.username,
            display_name: data.displayName,
          },
        },
      });

      if (signUpError) {
        console.error("Sign-up error:", signUpError);
        if (signUpError.message.includes("User already registered")) {
          toast.error("This email is already registered. Try signing in instead.");
          onSuccess();
        } else {
          throw signUpError;
        }
        setIsSubmitting(false);
        return;
      }

      if (!signUpData?.user) {
        throw new Error("Failed to create user account");
      }

      console.log("User account created successfully, ID:", signUpData.user.id);
      
      // Show success message with email confirmation instructions
      toast.success("Account created successfully! 🎉");
      toast.info("Please check your email to confirm your account before signing in.", {
        duration: 6000,
        description: "The confirmation email might take a few minutes to arrive. Don't forget to check your spam folder!"
      });
      
      onSuccess();
    } catch (error: any) {
      console.error("Sign-up process error:", error);
      toast.error(error.message || "Failed to create account. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <AccountDetailsForm
        initialData={accountData}
        onNext={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
